import {
  ControlledDropDown,
  CustomButton,
  DropdownType,
  FORMAT_PATTERN,
  formatPhoneNumber,
  Modal,
  SvgWarningNew,
} from '@ui-kit'
import {
  BUTTONS,
  COMM_TEMPLATE,
  emailTemplate,
  ENTITY_NAME,
  NAMESPACES,
  PHONE_TYPE_CATEGORY_VALUES,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import {
  addToaster,
  FaxOutboundTaskBoxProps,
  getRecipientsByEntityInstanceId,
  getRecipientsCorrespondingToEntityInstance,
  OrganizationOptions,
  RecipientFaxOutbound,
  SendManualCommunicationProps,
} from '@app-store'
import {
  getEntityName,
  getLatestActiveRecord,
  setPhoneTypeIdFunction,
} from '../../utils/helperFunctions'
import { useEffect, useState } from 'react'
import { RootState, store, useAppSelector } from '../../utils/agent.store'
import { useFormContext } from 'react-hook-form'
import { isReadOnlyUser } from '../../utils/permission'
import TemplateNameDropdown from './TemplateNameDropdown'
import { uniqueId } from 'lodash'
import { AddDataModal } from '../MultiLookupRowRender'

const TextOutboundTaskBox = ({
  isSendButtonDisabled,
  setIsSendButtonDisabled,
  previewTemplateData,
  setPreviewTemplateData,
  onSubmit,
  sendCommunicationTemplate,
  previewDataWithLink,
  setPreviewDataWithLink,
  ...rest
}: FaxOutboundTaskBoxProps) => {
  const props = rest as SendManualCommunicationProps
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    watch,
    setValue,
  } = useFormContext()
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    message: '',
    title: '',
  })

  const globalVariables = useAppSelector(
    (state: RootState) =>
      state?.workflowData?.workflowDefinition?.global_variables
  )
  const loading = useAppSelector((state: RootState) => state.loader.loading)

  const [toSmsFax, setToSmsFax] = useState<DropdownType<string>[]>([])

  const resourceId = props.task.resource_id
  const isCompleted = props.task.state === WORKFLOW_STATUS.COMPLETED

  if (resourceId && isCompleted) {
    const primaryOptions = toSmsFax.filter((item: { label: string }) =>
      item?.label?.includes('Primary')
    )

    if (primaryOptions[0]?.value && watch().to !== primaryOptions[0]?.value) {
      setValue('to', primaryOptions[0]?.value)
    }
  }

  const handleTemplateOptionChange = (e: DropdownType<string>) => {
    const templateId = e?.value
    setIsSendButtonDisabled(true)
    if (previewDataWithLink) {
      setPreviewDataWithLink(null)
    }
    setValue('template_name', templateId)
    if (previewTemplateData) {
      setPreviewTemplateData(null)
      // setValue('to', []) //TODO: ensure whether to use or not
    }
  }

  useEffect(() => {
    if (toSmsFax?.length === 0 && !loading)
      setError('to', { message: NAMESPACES.NO_PHONE_FOUND })
    else if (toSmsFax?.length) clearErrors('to')
  }, [toSmsFax, loading])

  useEffect(() => {
    const fetchRecipientOptions = async () => {
      const recordId =
        props?.task?.requestor_view?.record_id ??
        props?.task?.organization_view?.record_id
      const entityType =
        props?.task?.requestor_view?.entity_type ??
        (props?.task?.organization_view as OrganizationOptions)
          ?.organization_type
      if (recordId) {
        try {
          const consentPayload = {
            id: recordId,
            apiPayload: {
              select: [NAMESPACES.C_PATIENT_CONSENT],
              entity_name: 'patients',
              related_entities: {
                [NAMESPACES.C_PATIENT_CONSENT]: {
                  select: ['consent_type'],
                  related_entities: {
                    consent_type: {
                      select: ['name'],
                      filters: [
                        {
                          criteria: [
                            {
                              values: ['Text Messaging'],
                              operation: 'EQUALS',
                              field_name: 'name',
                            },
                          ],
                        },
                      ],
                    },
                  },
                  filters: [
                    {
                      criteria: [
                        {
                          values: [],
                          operation: 'EQUALS',
                          field_name: 'consent_type_id',
                        },
                      ],
                    },
                  ],
                },
              },
            },
          }

          const result = await getRecipientsCorrespondingToEntityInstance(
            consentPayload
          )
          if (!result.data?.c_patient_consents_patient?.length) {
            setConfirmModal({
              isOpen: true,
              title: NAMESPACES.TEXT_MSG_CONSENT_ERROR_TITLE,
              message: NAMESPACES.TEXT_MSG_CONSENT_ERROR_MSG,
            })
            return
          }
        } catch {
          setConfirmModal({
            isOpen: true,
            title: NAMESPACES.TEXT_MSG_CONSENT_ERROR_TITLE,
            message: NAMESPACES.TEXT_MSG_CONSENT_ERROR_MSG,
          })
        }

        const entityName = getEntityName(entityType ?? '')
        const basePayload =
          globalVariables?.[entityName as 'contacts' | 'patients' | 'providers']

        if (!basePayload) return
        const entityPayload = {
          ...basePayload,
          select:
            entityName === ENTITY_NAME.PATIENTS
              ? [NAMESPACES.C_PATIENT_PHONE]
              : basePayload?.select,
        }

        const updatedPhonePayload = setPhoneTypeIdFunction(
          entityPayload ?? {
            select: [],
            entity_name: '',
            related_entities: {},
          },
          PHONE_TYPE_CATEGORY_VALUES.PHONE,
          true
        )
        updatedPhonePayload.entity_name = 'patients'

        const payload: getRecipientsByEntityInstanceId = {
          id: recordId,
          apiPayload: updatedPhonePayload ?? {
            select: [],
            entity_name: '',
            related_entities: {},
          },
        }

        let relatedEntityName
        if (entityName === 'patients') {
          relatedEntityName =
            payload?.apiPayload?.select?.find((item: string) =>
              item?.toLowerCase()?.includes('phone')
            ) ?? NAMESPACES.C_PATIENT_PHONE
        } else {
          relatedEntityName =
            payload?.apiPayload?.select?.find((item: string) =>
              item?.toLowerCase()?.includes('phone')
            ) ?? payload?.apiPayload?.select?.[0]
        }

        const response = await getRecipientsCorrespondingToEntityInstance(
          payload
        )

        return response?.data?.[relatedEntityName]
      }
    }
    const fetchAndSetRecipients = async () => {
      try {
        const recipientResponse = await fetchRecipientOptions()
        const recipientOptions = recipientResponse?.map(
          (item: RecipientFaxOutbound) => ({
            label: item?.is_preferred
              ? formatPhoneNumber(
                  item.phone_number,
                  FORMAT_PATTERN.PHONE_INPUT
                ) + ' (Primary)'
              : formatPhoneNumber(
                  item?.phone_number,
                  FORMAT_PATTERN.PHONE_INPUT
                ),
            value: formatPhoneNumber(
              item.phone_number,
              FORMAT_PATTERN.PHONE_INPUT
            ),
          })
        )
        const filtered = recipientResponse?.filter(
          (item: RecipientFaxOutbound) => item?.is_preferred
        )

        const latestRecord = getLatestActiveRecord(
          filtered,
          emailTemplate.PHONE,
          true
        )
        const defaultRecipientOptionValue = formatPhoneNumber(
          latestRecord?.phone_number ?? '',
          FORMAT_PATTERN.PHONE_INPUT
        )
        if (defaultRecipientOptionValue)
          setValue('to', defaultRecipientOptionValue)
        setToSmsFax(recipientOptions || [])
      } catch {
        store.dispatch(
          addToaster({
            id: uniqueId(),
            type: 'error',
            message: 'Something went wrong',
          })
        )
      }
    }
    fetchAndSetRecipients()
  }, [
    props?.task?.requestor_view?.record_id,
    props?.task?.organization_view?.record_id,
  ])

  return (
    <div className="">
      <div className="flex justify-end gap-2">
        <CustomButton
          dataQaId="generate-send-enable-disable"
          disabled={
            isReadOnlyUser() ||
            !watch('template_name') ||
            //    !!props?.task?.resource_id ||
            !!previewTemplateData?.body
            //    props?.isDisabled
          }
          isFilled={true}
          className={`h-[2.375rem] flex gap-1 items-center`}
          type="button"
          tabIndex={props?.tabIndexOffset}
          onClick={() => onSubmit()}
          key={watch('template_name')?.toString()}
        >
          <span className={`font-primary`}>
            {COMM_TEMPLATE.GENERATE_PREVIEW}
          </span>
        </CustomButton>
        <CustomButton
          key={(watch('to') ?? [])?.toString()}
          dataQaId="send-communication-template"
          type="button"
          color="primary"
          widthClass="w-[5.563rem]"
          className="text-xs flex mb-4 mr-2"
          isFilled={true}
          onClick={sendCommunicationTemplate}
          tabIndex={props?.tabIndexOffset}
          disabled={
            isReadOnlyUser() ||
            isSendButtonDisabled ||
            props?.isDisabled ||
            !!props?.task?.resource_id ||
            !watch('to')
          }
        >
          <span className="text-sm font-light">{BUTTONS.SEND}</span>
        </CustomButton>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <TemplateNameDropdown
          handleTemplateOptionChange={handleTemplateOptionChange}
          {...props}
        />

        <ControlledDropDown
          options={toSmsFax}
          name="to"
          label={COMM_TEMPLATE.RECIPIENT_PHONE_NUMBER}
          isSearchable={false}
          errors={props?.isDisabled ? {} : errors}
          control={control}
          required={true}
          onChange={(value: DropdownType<string>) => {
            setValue('to', value?.value, { shouldValidate: true })
          }}
          dataQaId={'to'}
          disabled={props?.isDisabled}
          tabIndex={props?.tabIndexOffset}
          key={props?.task?.resource_id}
        />
      </div>
      <AddDataModal>
        <Modal
          isOpen={confirmModal.isOpen}
          closeModal={() =>
            setConfirmModal({ title: '', message: '', isOpen: false })
          }
          width="min-w-[30vw] max-w-[70%]"
        >
          <div className="flex flex-col gap-2 p-2">
            <div className="flex justify-center">
              <div className="flex items-center gap-2">
                <SvgWarningNew />
                <div className="font-semibold">
                  <p>{confirmModal.title}</p>
                </div>
              </div>
            </div>
            <p>{confirmModal.message}</p>
          </div>
          <div className="flex justify-end m-2 pt-2 border-t">
            <CustomButton
              onClick={() =>
                setConfirmModal({ message: '', title: '', isOpen: false })
              }
              className="bg-[#f6426f] !text-white"
            >
              OK
            </CustomButton>
          </div>
        </Modal>
      </AddDataModal>
      {previewTemplateData?.body ? (
        <div className="mt-4 bg-[#f6f6f6] relative pt-2">
          <p className="text-sm font-light absolute top-0 translate-y-[-50%] left-2">
            Text Message
          </p>
          <div
            dangerouslySetInnerHTML={{
              __html: previewTemplateData?.body,
            }}
            className="  overflow-x-auto outline-none px-2 h-[8.625rem] rounded-lg min-h-10 "
          />
        </div>
      ) : null}
    </div>
  )
}
export default TextOutboundTaskBox
